/**
 * Handles broken image errors in Native Interactive Site Map card components.
 *
 * When card images fail to load, replaces them with a placeholder image from the IRS domain.
 * Uses a single capture-phase error listener on the container to catch all image errors,
 * including dynamically rendered images (via innerHTML swaps on chip/tab/lot changes).
 * Error events don't bubble, but do propagate during capture phase.
 *
 * Pattern mirrors: pages/searchResults/components/images/handlerErrorImages.js
 *
 * @module nativeInteractiveSiteMap/handlers/imageErrorHandler
 */

import { fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';

const CARD_IMAGE_SELECTOR = '[data-siteplan-card-image]';
const PLACEHOLDER_CLASS = 'nhs-c-siteplan-card__image--placeholder';

/**
 * Removes all `<source>` elements within the closest `<picture>` element of a given image.
 * @param {HTMLImageElement} image - Reference to an HTML img element
 */
function removeSourceElements(image) {
  const picture = image.closest('picture');
  if (picture) {
    picture.querySelectorAll('source').forEach((element) => element.remove());
  }
}

/**
 * Replaces the source of a broken image element with a placeholder image.
 * @param {HTMLImageElement} image - The img element that failed to load
 * @param {string} placeholderImageUrl - URL to the placeholder image
 */
function replaceBrokenImageWithPlaceholder(image, placeholderImageUrl) {
  removeSourceElements(image);
  image.src = placeholderImageUrl;
  // add custom class to indicate that the image has been replaced with a placeholder
  image.classList.add(PLACEHOLDER_CLASS);
}

/**
 * Sets up error handling for card images within a container.
 * Listens for image load errors and replaces broken images with a placeholder.
 *
 * Uses a single capture-phase error listener to catch all image errors within
 * the container, including dynamically rendered images (via innerHTML swaps).
 * No re-binding needed—all current and future images are covered by one listener.
 *
 * @param {HTMLElement} container - The container element that holds card images
 * @param {string} placeholderImageUrl - URL to use as fallback for broken images
 * @returns {{ unbind: Function }} Object with unbind function for cleanup
 */
export default function createImageErrorHandler(container, placeholderImageUrl) {
  const subscription = fromEvent(container, 'error', { capture: true })
    .pipe(
      map((event) => event.target),
      // Already-swapped images carry the placeholder class — skipping them stops a
      // broken placeholder asset from re-triggering itself in an infinite loop.
      filter((target) => target?.matches?.(CARD_IMAGE_SELECTOR) && !target.classList.contains(PLACEHOLDER_CLASS)),
    )
    .subscribe((image) => replaceBrokenImageWithPlaceholder(image, placeholderImageUrl));

  return { unbind: () => subscription.unsubscribe() };
}